import { useQuery } from "@tanstack/react-query";
import {
  loadLocationDetails,
  syncAccessibility,
  syncAccessibilityEnterprise,
} from "../../../apis/venueDetails";
import {
  AccessibilityCategory,
  AccessibilityCategoryHybrid,
  AccessibilityFilters,
  BusinessNote,
  BusinessNoteAction,
  BusinessNoteTask,
  businessNotesAll,
  ComplexCategoriesData,
  VenueDetails,
} from "../../../types";

type UseVenueDetailsBySlugProps = {
  slug: string;
  latitude: string;
  longitude: string;
  accessToken: string;
  enabled: boolean;
  type?: "Basic" | "Complex";
};

export type VenueDetailsBySlugResponse = {
  details: VenueDetails;
  accesibilityCategories?: AccessibilityCategory[]; // Only for Basic venues
  accesibilityCategoriesHybrid?: AccessibilityCategoryHybrid[]; // Deprecated - hybrid venues now use Enterprise data
  accesibilityCategoriesEnterprise?: ComplexCategoriesData[]; // For Complex (Hybrid + Enterprise) venues
  floorTypeOptions?: { id: number; title: string }[]; // For Complex venues
  enterpriseBusinessNotesActions?: BusinessNoteAction[]; // For Complex venues
  businessNotesTasks?: {
    Custom: BusinessNoteTask[];
    General: BusinessNoteTask[];
  }; // For Complex venues
  review_tags?: ComplexCategoriesData[]; // For Complex venues
  businessNotesAreaLocationTypes?: string[]; // For Complex venues
  businessNotesForMappers?: BusinessNote[]; // For Complex venues
  businessNotesAll?: businessNotesAll[]; // For Complex venues
  accessibility_filters?: AccessibilityFilters; // For Complex venues
};

export function useVenueDetailsBySlug({
  slug,
  latitude,
  longitude,
  accessToken,
  enabled,
  type,
}: UseVenueDetailsBySlugProps) {
  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "loadVenueDetailsBySlug",
      slug,
      latitude,
      longitude,
      accessToken,
      type,
    ],
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    queryFn: async () => {
      console.log("useVenueDetailsBySlug slug", slug);
      const detailsResponse = await loadLocationDetails(slug, {
        latitude: latitude,
        longitude: longitude,
        token: accessToken,
        type: type,
      });

      let accesibilityCategories = [];

      if (detailsResponse.templateType != "Basic") {
        // Both Hybrid and Enterprise venues now use sync-spaces
        const {
          accesibilityCategoriesEnterprise,
          floorTypeOptions,
          enterpriseBusinessNotesActions,
          businessNotesTasks,
          review_tags,
          businessNotesAreaLocationTypes,
          businessNotesForMappers,
          businessNotesAll,
          accessibility_filters
        } = await syncAccessibilityEnterprise(
          detailsResponse.business.id,
          slug
        );
        return {
          details: detailsResponse,
          accesibilityCategoriesEnterprise,
          floorTypeOptions,
          enterpriseBusinessNotesActions,
          businessNotesTasks,
          review_tags,
          businessNotesAreaLocationTypes,
          businessNotesForMappers,
          businessNotesAll,
          accessibility_filters
        } as VenueDetailsBySlugResponse;
      } else {
        // Basic venues
        accesibilityCategories = await syncAccessibility(
          detailsResponse.business.id,
          slug
        );
        return {
          details: detailsResponse,
          accesibilityCategories,
        } as VenueDetailsBySlugResponse;
      }
    },
    enabled,
  });
  return {
    data,
    error,
    isLoading: isLoading || isFetching,
    refetch,
  };
}
